import IconTrashSolid from '@/assets/icons/font_awesome/trash-solid.svg?react';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_workspace } from '@/hooks/store/use_slices';
import { toast, useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_workspace_child_entity_delete } from '@/state/workspace/child_entity/remote_workspace_child_entity_delete';
import { DTO__workspace_child_enity } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/dto_workspace_child_enity';
import { RequestWorkspaceChildEntityDelete } from '@shared/dto/+workspace/workspace_entity/_requests/request_workspace_child_entity_delete';
import { MouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_PRIMITIVE__button_with_icon_animated } from '../../../components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { UI_PRIMITIVE__progress_bar } from '../../../components/ui/UI_PRIMITIVE__progress_bar';
import { UI_PRIMITIVE__tooltip } from '../../../components/ui/UI_PRIMITIVE__tooltip';

interface Props {
  required_permission: string;
  entity: DTO__workspace_child_enity;
  isSelected: boolean;
  setIsButtonDeleteClicked: (value: boolean) => void;
}

export function ButtonWorkspaceChildEntityDelete({ required_permission, entity, setIsButtonDeleteClicked }: Props) {
  const dispatch = use_dispatch();

  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const { dismiss } = useToast();

  const [is_clicked, set_is_clicked] = useState(false);

  const ref_current_toast_id = useRef('');

  /*
   * EVENTS
   */

  function onTimerComplete() {
    set_is_clicked(false);
    dismiss(ref_current_toast_id.current);
    ref_current_toast_id.current = '';
  }

  async function onClick(e: MouseEvent) {
    e.stopPropagation();

    if (is_clicked) {
      onTimerComplete();

      const request = new RequestWorkspaceChildEntityDelete();
      request.workspace_id = entity.workspace_id;
      request.entity_type = entity.type;
      request.entity_id = entity._id;

      await dispatch(remote_workspace_child_entity_delete(request));

      setIsButtonDeleteClicked(false);
    } else {
      set_is_clicked(true);
      setIsButtonDeleteClicked(true);

      let toast_new = toast({
        title: t(i18n_base_ui.toasts.child_entity_delete.title),
        duration: CONSTANTS_TIME__client.TOAST_DURATION_WORKSPACE_CHILD_ENTITY_DELETE,
        description: (
          <UI_PRIMITIVE__progress_bar
            timeout_ms={CONSTANTS_TIME__client.TOAST_DURATION_WORKSPACE_CHILD_ENTITY_DELETE}
            text={t(i18n_base_ui.toasts.child_entity_delete.description, { entity_type: entity.type })}
            {...{ onTimerComplete }}
          />
        ),
      });

      ref_current_toast_id.current = toast_new.id;
    }
  }

  if (!current_workspace_user.is_feature_pertmitted(required_permission)) return null;

  return (
    <UI_PRIMITIVE__tooltip tooltip_title={t(i18n_base_ui.button.workspace_child_entity_delete.tooltip)}>
      <UI_PRIMITIVE__button_with_icon_animated
        variant="no-border-on-bar"
        size="square-5"
        Icon={IconTrashSolid}
        className="min-w-7 p-0"
        iconClassNameBase={`!w-3 !h-3`}
        iconClassNameHovered={`${is_clicked ? '!text-red-400' : '!text-bar-foreground'}`}
        iconClassNameNonHovered={`${is_clicked ? '!text-red-700' : '!text-bar'}`}
        {...{ onClick }}
      />
    </UI_PRIMITIVE__tooltip>
  );
}
